import { BuildClass, Do, Sleep, fsmData, guid, runPromisesSequentially, } from '../../../universal';
import { React, _ } from '../../lib';
import { Button, FormButton, FormButtonSet } from './buttons';
import { checkReadOnly } from './editable-cells';
import { ListGrid, } from './list-grid';
import { CJSX, ConditionalObject, useEffectCompare, useRSInstance, useStateSync, } from './meta-types';
import { stubDiv, stubEditableGridInstance, stubFocusable, stubInput, stubListGrid, } from './stubs';
import { Textbox } from './textbox';
import { Toolbar } from './toolbar';
const getDefaultState = (props, seed) => ({
    data: getDefaultDataState(props, seed),
    view: props.view ?? {},
    focusedCell: null,
    message: '',
    msgColor: '',
    isLoading: false,
    showTick: false,
});
const EditableGridComponent = (props, ref) => {
    const seed = React.useRef({ index: 1, circle: 0.0 });
    const rs = useRSInstance({
        props: props,
        refs: {
            seed: seed,
            wrapper: React.useRef(stubDiv),
            listGrid: React.useRef(stubListGrid),
            cellRefs: React.useRef({}),
        },
        defaultState: React.useCallback(p => getDefaultState(p, seed), []),
        actionToDelta: getPartialStateFromAction,
    });
    useEffectCompare(() => {
        rs.refs.seed.current = { index: 1, circle: 0.0 };
        rs.dispatch([Action.UpdateData, getDefaultDataState(rs.props, rs.refs.seed)]);
    }, rs.props.data);
    useStateSync({
        stateVal: rs.state.data,
        propVal: rs.props.value,
        setState: data => {
            if (data) {
                rs.dispatch([
                    Action.UpdateData,
                    getDefaultDataState(rs.props, rs.refs.seed, data),
                ]);
            }
        },
        setProp: value => {
            rs.props.onUpdate?.(_.values(value));
        },
    });
    useStateSync({
        stateVal: rs.state.focusedCell,
        propVal: rs.props.focusedCell,
        setState: v => {
            rs.dispatch([Action.UpdateFocusedCell, v]);
        },
        setProp: v => {
            rs.props.onUpdateFocusedCell?.(v);
        },
    });
    const updateSearchBoxText = React.useCallback((v) => {
        changeSearchText(rs, v);
    }, [rs]);
    const rCount = {
        filtered: rs.refs.listGrid.current
            ?.getFilteredRecords(rs.state.view?.searchText ?? '')
            .filter(x => !x.deleted && x.original != null).length ?? 0,
        total: _.filter(rs.state.data, x => !x.deleted && x.original != null).length,
    };
    const onUpdateView = React.useCallback((v) => {
        rs.dispatch([Action.UpdateView, { view: v }]);
    }, [rs]);
    useStateSync({
        propVal: rs.props.view ?? {},
        stateVal: rs.state.view,
        setProp: v => rs.props.onUpdateView?.(v),
        setState: v => {
            onUpdateView(v);
        },
        disabled: !rs.props.view && !rs.props.onUpdateView,
    });
    const focusEmptyRowWrapper = React.useCallback(() => {
        focusEmptyRow(rs).catch(err => {
            console.error('Error focusing empty row', err);
        });
    }, [rs]);
    {
        const refs = rs.refs.cellRefs.current;
        _.forEach(rs.state.data, row => {
            if (!_.has(refs, row.pk)) {
                refs[row.pk] = {};
            }
            const item = refs[row.pk];
            _.forEach(rs.props.fields, field => {
                if (item && !item[field.key]) {
                    item[field.key] = {
                        current: stubFocusable(stubInput),
                    };
                }
            });
        });
    }
    React.useImperativeHandle(ref, () => ({
        getElement: () => rs.refs.wrapper.current,
        listGrid: () => rs.refs.listGrid.current,
        getDefaultDataState: () => getDefaultDataState(rs.props, rs.refs.seed),
        getChangeDelta: () => getChangeDeltaInner(rs),
        isChanged: () => getChangeDeltaInner(rs).isChanged,
        reset: () => {
            onReset(rs);
        },
        save: async () => onSave(rs),
        focusNewRow: () => {
            focusEmptyRowWrapper();
        },
        recordCount: rCount,
    }));
    return (React.createElement("div", { ref: rs.refs.wrapper, className: BuildClass({
            'ui5 ui5-editable-grid': true,
            readonly: props.readOnly ?? false,
            [props.className ?? '']: true,
            noToolbar: !(props.showToolbar ?? true),
            noSaveButtons: !props.onSave,
        }) },
        React.createElement(CJSX, { cond: props.showToolbar ?? true },
            React.createElement(Toolbar, { lhs: React.createElement(React.Fragment, null), rhs: React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "record-count" }, rCount.filtered == rCount.total
                        ? `${rCount.total} records`
                        : `Filtered to show ${rCount.filtered} / ${rCount.total} records`),
                    React.createElement("div", { className: "search-wrapper" },
                        React.createElement("img", { src: "/static/img/svg/search.svg" }),
                        React.createElement(Textbox, { className: "search", placeholder: "Search...", type: "search", value: rs.state.view?.searchText ?? '', onUpdate: updateSearchBoxText })),
                    React.createElement(CJSX, { cond: rs.props.appendable != null },
                        React.createElement(Button, { className: "add-button", type: "borderless", title: "Focuses the empty row in the grid to add a new record", onClick: focusEmptyRowWrapper },
                            React.createElement("img", { src: "/static/img/svg/plus-black.svg" }),
                            "Add Record"))), widthLHS: 0 })),
        React.createElement(ListGrid, { ref: rs.refs.listGrid, data: _.filter(rs.state.data, x => !x.deleted &&
                (!x.original || !props.filter || props.filter(x.original))), pk: x => x.pk, disableFocusControl: true, disableClickHandlers: true, multiple: false, value: null, onUpdate: () => { }, readOnly: true, searchText: rs.state.view?.searchText, view: _.omit(rs.state.view, ['searchText']), onUpdateView: v => {
                onUpdateView({
                    ...v,
                    searchText: rs.state.view?.searchText,
                });
            }, grouping: Do(() => {
                const grp = props.grouping;
                return grp == null
                    ? undefined
                    : {
                        key: x => grp.key(x.viewing),
                        name: (gID, x) => grp.name(gID, x.viewing),
                        sort: group => grp.sort({
                            ID: group.ID,
                            Name: group.Name,
                            Records: group.Records.map(x => x.viewing),
                        }),
                        reverse: grp.reverse,
                        gaps: grp.gaps,
                    };
            }), defaultSortingKey: props.defaultSortingKey, defaultSortingReverse: props.defaultSortingReverse, classRow: props.classRow
                ? x => props.classRow?.(x?.viewing ?? null) ?? ''
                : undefined, tooltipRow: props.tooltipRow
                ? x => props.tooltipRow?.(x?.viewing ?? null) ?? ''
                : undefined, height: v => props.height?.(v.viewing) ?? 28, sortable: props.sortable, style: props.style, onKeyDown: (ev, defaultEvent) => {
                console.log({ ev, defaultEvent });
            }, fields: _.compact([
                ConditionalObject(!(props.readOnly ?? false), () => ({
                    key: 'change-indicator',
                    lbl: '',
                    lblLong: '[Changed]',
                    lblDesc: 'Colour-coded based on whether the record has changed (green), has errors (red), or is unchanged (grey)',
                    className: () => 'noselect',
                    tooltip: x => getIndicatorRow(rs, x).message,
                    text: () => '',
                    sortable: false,
                    searchable: false,
                    display: x => React.createElement("div", { className: getIndicatorRow(rs, x).type }),
                })),
                ...fsmData(props.fields, {
                    map: F => buildField(rs, F),
                }),
                ConditionalObject(!(props.readOnly ?? false), () => ({
                    key: 'delete-reset-button',
                    lbl: '',
                    lblLong: '[Delete/Reset]',
                    lblDesc: 'Delete / reset buttons',
                    className: () => 'noselect',
                    text: () => '',
                    searchable: false,
                    sortable: false,
                    tooltip: x => {
                        switch (checkRecordDeletableResetable(rs, x)) {
                            case 'delete':
                                return 'Delete this record';
                            case 'reset':
                                return 'Reset this record';
                            default:
                                return '';
                        }
                    },
                    display: x => {
                        switch (checkRecordDeletableResetable(rs, x)) {
                            case 'delete':
                                return (React.createElement("img", { className: "delete-icon", src: "/static/img/cross.png" }));
                            case 'reset':
                                return (React.createElement("img", { className: "reset-icon", src: "/static/img/undo.png" }));
                            default:
                                return React.createElement(React.Fragment, null);
                        }
                    },
                    onClick: x => {
                        switch (checkRecordDeletableResetable(rs, x)) {
                            case 'delete':
                                actionDelete(rs, x);
                                break;
                            case 'reset':
                                actionResetRow(rs, x);
                                break;
                            default:
                                break;
                        }
                    },
                })),
            ]) }),
        React.createElement(CJSX, { cond: Boolean(props.onSave) },
            React.createElement(FormButtonSet, { loading: rs.state.isLoading, msg: rs.state.message, tick: rs.state.showTick },
                React.createElement(FormButton, { lbl: "Save", onClick: () => {
                        onSave(rs).catch(err => {
                            rs.dispatch([
                                Action.UpdateFormState,
                                {
                                    message: String(err),
                                    isLoading: false,
                                    showTick: false,
                                },
                            ]);
                        });
                    } }),
                React.createElement(FormButton, { lbl: "Reset", onClick: () => {
                        onReset(rs);
                    } })))));
};
const getDefaultDataState = (props, seed, dataOverride) => {
    const data = _.fromPairs(dataOverride
        ? dataOverride.map(x => [x.pk, x])
        : props.data.map(x => [
            props.pk(x),
            {
                pk: props.pk(x),
                blankIndex: 0,
                original: x,
                viewing: x,
                current: x,
                deleted: false,
            },
        ]));
    return addEmptyRows(data, props.appendable, seed);
};
const buildField = (rs, F) => ({
    key: F.key,
    disabled: F.disabled ?? false,
    className: x => BuildClass({
        [F.className?.(x?.current) ?? '']: true,
        [x == null ? '' : (F.cell.className?.(F.value(x.current)) ?? '')]: true,
        'cell-outer': true,
        [x ? `indicator-${getIndicatorCell(rs, F, x).type}` : '']: true,
    }),
    tooltip: x => {
        const indicator = getIndicatorCell(rs, F, x);
        if (indicator.type == 'red') {
            return indicator.message;
        }
        return '';
    },
    text: x => (F.text ? F.text(x.current) : (F.cell.text?.(F.value(x.current)) ?? '')),
    display: x => {
        const ref = rs.refs.cellRefs.current[x.pk][F.key];
        if (!ref) {
            console.error('Missing ref for', F.key, x);
            return React.createElement(React.Fragment, null);
        }
        return F.cell.cmpt({
            record: x?.current,
            value: F.value(x?.current),
            ref: ref,
            searchText: rs.state.view?.searchText || null,
            readOnly: rs.props.readOnly || checkReadOnly(F?.readOnly, x?.current),
            onUpdate: (value, commit) => {
                const isReadOnly = rs.props.readOnly ||
                    (_.isFunction(F.cell.readOnly)
                        ? F.cell.readOnly(x.current)
                        : F.cell.readOnly);
                if (isReadOnly) {
                    console.error('Read-only component triggered update', F.key);
                    rs.dispatch([Action.UpdateData, rs.state.data]);
                    return;
                }
                const newVal = (F.cell.proxyValue ?? _.identity)(value);
                const currRecord = { ...x.current };
                F.onUpdate(newVal, currRecord);
                const updateViewing = commit && !(rs.props.deferSorting ?? true);
                rs.dispatch([
                    Action.UpdateRecord,
                    x.pk,
                    {
                        ...x,
                        current: { ...currRecord },
                        viewing: updateViewing ? { ...currRecord } : x.viewing,
                    },
                ]);
            },
            onFocus: () => {
                rs.dispatch([Action.UpdateFocusedCell, { pk: x.pk, field: F.key }]);
            },
            onBlur: () => {
                rs.dispatch([Action.UpdateFocusedCell, null]);
            },
            onCommit: () => {
                if (!(rs.props.deferSorting ?? true)) {
                    rs.dispatch([
                        Action.UpdateRecord,
                        x.pk,
                        {
                            ...x,
                            viewing: x.current,
                        },
                    ]);
                }
            },
        });
    },
    lbl: F.lbl,
    lblLong: F.lblLong,
    lblDesc: F.lblDesc,
    tooltipBubble: F.tooltipBubble,
    preSort: Do(() => {
        const blank_record = rs.props.appendable?.();
        return (x, isRev) => {
            const is_blank = x.original == null && _.isEqual(x.viewing, blank_record);
            return [isRev ? !is_blank : is_blank, is_blank ? x.blankIndex : 0];
        };
    }),
    sortVal: Do(() => {
        if (F.sortVal) {
            return x => F.sortVal?.(x.viewing);
        }
        if (F.cell.sortVal) {
            return x => F.cell.sortVal?.(F.value(x.viewing));
        }
        if (F.value) {
            return x => F.value(x.viewing);
        }
        return () => null;
    }),
    sortReverseDefault: F.sortReverseDefault,
    sortable: F.sortable,
    groupVal: Do(() => {
        const fn = F.groupVal;
        if (fn != null) {
            return x => fn(x.viewing);
        }
        return undefined;
    }),
    groupName: Do(() => {
        const fn = F.groupName;
        if (fn != null) {
            return (gID, record) => fn(gID, record.viewing);
        }
        return undefined;
    }),
    groupSort: Do(() => {
        const fn = F.groupSort;
        if (fn != null) {
            return group => fn({
                ID: group.ID,
                Name: group.Name,
                Records: group.Records.map(x => x.viewing),
            });
        }
        return undefined;
    }),
    filterValues: F.filterValues,
    colorVal: F.colorVal ? x => F.colorVal?.(x.current) ?? 0 : undefined,
    colorRange: F.colorRange,
});
export const getEGChangeDelta = (current, newRecord) => {
    const nr = newRecord?.();
    const isEmpty = (x) => !x.original && _.isEqual(x.current, nr);
    const changed = fsmData(current, {
        filter: x => x.original && !_.isEqual(x.original, x.current),
        map: x => {
            const keys = _.uniq([
                ..._.keys(x.original),
                ..._.keys(x.current),
            ]);
            const is_changed_inner = fsmData(keys, {
                filter: k => !x.original || !_.isEqual(x.original[k], x.current[k]),
                map: k => [k, x.current[k]],
            });
            return [x.pk, _.fromPairs(is_changed_inner)];
        },
    });
    const appended = fsmData(current, {
        filter: x => !x.original && !isEmpty(x) && !x.deleted,
        map: x => [String(x.pk), x.current],
    });
    const deleted = fsmData(current, {
        filter: x => x.deleted && x.original,
        map: x => x.pk,
    });
    return {
        data: fsmData(current, {
            filter: x => !isEmpty(x),
            map: x => x.current,
        }),
        appended: appended.map(x => x[1]),
        appendedIDs: appended.map(x => x[0]),
        updated: _.fromPairs(changed),
        deleted: deleted,
        isChanged: changed.length > 0 || deleted.length > 0 || appended.length > 0,
    };
};
const getChangeDeltaInner = (rs) => getEGChangeDelta(_.values(rs.state.data), rs.props.appendable);
const checkRecordDeletableResetable = (rs, x) => {
    const nr = rs.props.appendable?.();
    const existing = x.original != null;
    const changed = !_.isEqual(x.original, x.current);
    if (existing) {
        if (!changed && rs.props.deletable) {
            return 'delete';
        }
        else if (!changed) {
            return null;
        }
        return 'reset';
    }
    if (_.isEqual(nr, x.current)) {
        return null;
    }
    return 'delete';
};
const getIndicatorRow = (rs, rec) => {
    const blank = rec.original == null && _.isEqual(rec.current, rs.props.appendable?.());
    const errors = () => _.flatten(fsmData(rs.props.fields, {
        filter: x => !x.disabled,
        map: field => getErrorsInCell(field, rec),
    }));
    if (blank) {
        return {
            type: 'white',
            message: 'Empty row',
        };
    }
    else if (errors().length > 0) {
        return {
            type: 'red',
            message: errors().join(', '),
        };
    }
    else if (_.isEqual(rec.current, rec.original)) {
        return {
            type: 'grey',
            message: 'Record unchanged',
        };
    }
    return {
        type: 'green',
        message: 'Record changed',
    };
};
const getIndicatorCell = (rs, field, rec) => {
    const blank_record = rs.props.appendable?.();
    const blank = rec.original == null && _.isEqual(rec.current, blank_record);
    const errors = getErrorsInCell(field, rec);
    const curVal = field.value(rec.current);
    const type = () => {
        if (field.hideIndicators ?? false) {
            return 'white';
        }
        else if (blank &&
            blank_record &&
            _.isEqual(curVal, field.value(blank_record))) {
            return 'white';
        }
        else if (errors.length > 0) {
            return 'red';
        }
        else if (blank_record && _.isEqual(curVal, field.value(blank_record))) {
            return 'grey';
        }
        else if (rec.original && _.isEqual(curVal, field.value(rec.original))) {
            return 'grey';
        }
        return 'green';
    };
    return {
        type: type(),
        message: errors.join(', '),
    };
};
const getErrorsInCell = (field, rec) => {
    const value = field.value(rec.current);
    return fsmData([...(field.validation ?? []), ...(field.cell.validation ?? [])], {
        filter: x => x && !x.req(value),
        map: x => `${field.lbl}: ${x.msg}`,
    });
};
const changeSearchText = (rs, searchText) => {
    rs.dispatch([Action.UpdateSearchText, { searchText: searchText }]);
};
const updateView = (rs, view) => ({
    data: _.mapValues(rs.state.data, x => ({
        ...x,
        viewing: x.current,
    })),
    view: view,
});
const actionResetRow = (rs, record) => {
    if (record.original) {
        rs.dispatch([
            Action.UpdateRecord,
            record.pk,
            {
                ...record,
                viewing: record.original,
                current: record.original,
                deleted: false,
            },
        ]);
    }
    else {
        rs.dispatch([Action.DeleteRecord, { pk: record.pk }]);
    }
    rs.dispatch([
        Action.UpdateFormState,
        { message: '', isLoading: false, showTick: false },
    ]);
};
const focusEmptyRow = async (rs) => {
    if (!rs.props.appendable) {
        return;
    }
    const blank = rs.props.appendable();
    const record = _.first(_.filter(rs.state.data, x => !x.deleted && x.original == null && _.isEqual(x.current, blank)));
    if (!record) {
        console.warn('Could not find newly-created row to focus');
        return;
    }
    rs.refs.listGrid.current?.getList().updateScrollPosition([String(record.pk)]);
    const tryToSelect = () => {
        const recordRefs = rs.refs.cellRefs.current[record.pk];
        if (!recordRefs) {
            return false;
        }
        const cell = fsmData(rs.props.fields, {
            filter: f => recordRefs[f.key]?.current?.focus != null,
            map: f => recordRefs[f?.key ?? '']?.current,
            takeFirst: true,
        });
        if (cell) {
            cell.select();
            return true;
        }
        return false;
    };
    let done = false;
    await runPromisesSequentially(_.range(20).map(() => async () => {
        if (done) {
            return;
        }
        const succeeded = tryToSelect();
        if (succeeded) {
            done = true;
        }
        await Sleep(50);
    }));
};
const actionDelete = (rs, record) => {
    if (!rs.props.deletable && record.original != null) {
        return;
    }
    rs.dispatch([
        Action.UpdateRecord,
        record.pk,
        {
            ...record,
            deleted: true,
        },
    ]);
    rs.dispatch([
        Action.UpdateFormState,
        { message: '', isLoading: false, showTick: false },
    ]);
};
const onReset = (rs) => {
    rs.refs.seed.current = { index: 1, circle: 0.0 };
    rs.dispatch([Action.UpdateData, getDefaultDataState(rs.props, rs.refs.seed)]);
    rs.dispatch([
        Action.UpdateFormState,
        { message: '', isLoading: false, showTick: false },
    ]);
};
const onSave = async (rs) => {
    if (!rs.props.onSave) {
        console.error('No `onSave` property, but triggered the save event');
        return;
    }
    const delta = getChangeDeltaInner(rs);
    if (!delta.isChanged) {
        rs.dispatch([
            Action.UpdateFormState,
            { message: 'Nothing to update', isLoading: false, showTick: false },
        ]);
        setTimeout(() => {
            rs.dispatch([
                Action.UpdateFormState,
                { message: '', isLoading: false, showTick: false },
            ]);
        }, 500);
        return;
    }
    rs.dispatch([
        Action.UpdateFormState,
        { message: '', isLoading: true, showTick: false },
    ]);
    const result = await new Promise(resolve => {
        rs.props.onSave?.(delta, resolve);
    });
    if (result !== true) {
        rs.dispatch([
            Action.UpdateFormState,
            { message: result, isLoading: false, showTick: false },
        ]);
        return;
    }
    rs.dispatch([
        Action.UpdateFormState,
        { message: '', isLoading: false, showTick: true },
    ]);
    setTimeout(() => {
        rs.dispatch([
            Action.UpdateFormState,
            { message: '', isLoading: false, showTick: false },
        ]);
    }, 500);
};
const addEmptyRows = (data, appendable, seed) => {
    if (!appendable) {
        return data;
    }
    const blank_record = appendable();
    const blank_records = _.filter(data, x => !x.deleted && x.original == null && _.isEqual(x.current, blank_record));
    if (blank_records.length === 1) {
        return data;
    }
    if (blank_records.length > 1) {
        const keys_to_remove = blank_records.slice(1).map(x => x.pk);
        return _.omit(data, keys_to_remove);
    }
    const pk = gridGuid(seed);
    return {
        ...data,
        [pk]: {
            pk: pk,
            blankIndex: seed.current.index,
            deleted: false,
            original: null,
            viewing: blank_record,
            current: blank_record,
        },
    };
};
const gridGuid = (seed) => {
    seed.current = {
        circle: (seed.current.circle + Math.PI) % 1,
        index: seed.current.index + 1,
    };
    return guid(seed.current.circle);
};
var Action;
(function (Action) {
    Action[Action["UpdateState"] = 0] = "UpdateState";
    Action[Action["UpdateData"] = 1] = "UpdateData";
    Action[Action["UpdateRecord"] = 2] = "UpdateRecord";
    Action[Action["UpdateFormState"] = 3] = "UpdateFormState";
    Action[Action["DeleteRecord"] = 4] = "DeleteRecord";
    Action[Action["UpdateSearchText"] = 5] = "UpdateSearchText";
    Action[Action["UpdateView"] = 6] = "UpdateView";
    Action[Action["UpdateFocusedCell"] = 7] = "UpdateFocusedCell";
})(Action || (Action = {}));
const getPartialStateFromAction = (rs, p) => {
    switch (p[0]) {
        case Action.UpdateState:
            return p[1](rs.state);
        case Action.UpdateData:
            const data = addEmptyRows(p[1], rs.props.appendable, rs.refs.seed);
            return { data: data };
        case Action.UpdateRecord:
            return getPartialStateFromAction(rs, [
                Action.UpdateData,
                { ...rs.state.data, [p[1]]: p[2] },
            ]);
        case Action.DeleteRecord:
            return getPartialStateFromAction(rs, [
                Action.UpdateData,
                _.pickBy(rs.state.data, x => x.pk == p[1].pk),
            ]);
        case Action.UpdateFormState:
            return {
                message: p[1].message,
                isLoading: p[1].isLoading,
                showTick: p[1].showTick,
            };
        case Action.UpdateSearchText:
            return {
                view: {
                    ...rs.state.view,
                    searchText: p[1].searchText,
                },
            };
        case Action.UpdateView:
            return updateView(rs, p[1].view);
        case Action.UpdateFocusedCell:
            return {
                focusedCell: p[1] ? { pk: p[1].pk, field: p[1].field } : null,
            };
    }
};
export const useExternalGridState = () => {
    const gridRef = React.useRef(stubEditableGridInstance);
    const [gridState, setGridState] = React.useState();
    React.useEffect(() => {
        if (gridRef.current == null || gridState != null) {
            return;
        }
        setGridState(_.values(gridRef.current.getDefaultDataState()));
    }, [gridRef.current]);
    return { gridState, setGridState, gridRef };
};
export const EditableGrid = React.forwardRef(EditableGridComponent);
//# sourceMappingURL=editable-grid.js.map